import React from 'react'
import classNames from 'classnames'
import { Trans, t } from '@lingui/macro'
import { Box } from 'front-end/src/components'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import { brandColors } from 'shared/basicPalette'

import { IconXFull } from 'front-end/src/components/common/icon/IconXFull'
import { IconCheckFull } from 'front-end/src/components/common/icon/IconCheckFull'
import { IconCircleEmpty } from 'front-end/src/components/common/icon/IconCircleEmpty'
import { hideModal } from 'front-end/src/components/common/modal/ModalService'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import {
  useRemoveAttendeesFrom3dPartyCalendarsEventSeriesLazyQuery,
  useRemoveAttendeesFrom3dPartyCalendarsLazyQuery,
  useRemoveInviteeFromSeriesMutation,
  useRemoveInviteeMutation,
  useResendInvitationEmailEventSeriesMutation,
  useResendInvitationEmailMutation,
  useSendEventRegistrationEmailMutation,
  useSetEventInviteeAttendanceFlagEventSeriesMutation,
  useSetEventInviteeAttendanceFlagMutation,
  useSetEventInviteePresentFlagEventSeriesMutation,
  useSetEventInviteePresentFlagMutation,
  useSyncAttendeesStatusWith3dPartyCalendarsLazyQuery
} from './InviteeActionsMenuList.codegen'

import './InviteeActionsMenuList.less'
import { InviteeItemFragment } from './AttendanceTab.codegen'
import { useModal } from 'front-end/src/app/hooks/useModal'
import { InviteeRemoveModal } from 'front-end/src/pages/manage-events/event-edit/tabs/attendance/InviteeRemoveModal'
import { MarkAsAcceptedModal } from 'front-end/src/pages/manage-events/event-edit/tabs/attendance/MarkAsAcceptedModal'
import { Portal } from '@chakra-ui/react'

type SelectProps = {
  refetchInvitees: () => any
  eventId: number
  selectedInvitees: InviteeItemFragment[]
  groupId?: number
}

type InviteeActionsMenuListProps = {
  selectProps: SelectProps
  allowAttendance: boolean
  allowRemove: boolean
  reasonNotAllowRemove?: string | null
  isRecurringSeries?: boolean
  isRecurringInstance?: boolean
}

export const InviteeActionsMenuList = ({
  selectProps,
  isRecurringSeries = false,
  isRecurringInstance = false,
  allowAttendance,
  reasonNotAllowRemove,
  allowRemove
}: InviteeActionsMenuListProps) => {
  const [removeInvitee] = useRemoveInviteeMutation()
  const [removeInviteeFromSeries] = useRemoveInviteeFromSeriesMutation()

  const [removeAttendeesFrom3dPartyCalendars] =
    useRemoveAttendeesFrom3dPartyCalendarsLazyQuery()
  const [removeAttendeesFrom3dPartyCalendarsEventSeries] =
    useRemoveAttendeesFrom3dPartyCalendarsEventSeriesLazyQuery()

  const [resendInvitationEmail] = useResendInvitationEmailMutation()
  const [resendInvitationEmailEventSeries] =
    useResendInvitationEmailEventSeriesMutation()

  const [sendEventRegistrationEmailMutation] =
    useSendEventRegistrationEmailMutation()

  const [setEventInviteePresentFlag] = useSetEventInviteePresentFlagMutation()
  const [setEventInviteePresentFlagEventSeries] =
    useSetEventInviteePresentFlagEventSeriesMutation()

  const [setEventInviteeAttendanceFlag] =
    useSetEventInviteeAttendanceFlagMutation()
  const [setEventInviteeAttendanceFlagEventSeries] =
    useSetEventInviteeAttendanceFlagEventSeriesMutation()

  const [syncAttendeesStatusWith3dPartyCalendars] =
    useSyncAttendeesStatusWith3dPartyCalendarsLazyQuery()

  const { open: openRemoveModal, currentModal: removeModal } = useModal()
  const { open: openMarkAsAcceptedModal, currentModal: markAsAcceptedModal } =
    useModal()

  const { eventId } = selectProps
  const styleDot = {
    w: '12px',
    h: '12px',
    borderRadius: '50%',
    mr: '8px'
  }

  const confirmInviteeRemoval = async (eventId: number) => {
    const { refetchInvitees, selectedInvitees } = selectProps
    openRemoveModal(InviteeRemoveModal, {
      selectedInvitees: selectedInvitees
    }).then(async ({ sendCancellationEmail }) => {
      const emails: string[] = []
      await Promise.all(
        selectedInvitees.map(async ({ email, user_id, account_name }) => {
          emails.push(email || account_name || '')

          if (isRecurringSeries) {
            await removeInviteeFromSeries({
              variables: {
                eventId,
                userId: user_id,
                sendCancellationEmail
              }
            })
          } else {
            await removeInvitee({
              variables: {
                eventId,
                userId: user_id,
                sendCancellationEmail
              }
            })
          }
        })
      )

      if (isRecurringSeries) {
        await removeAttendeesFrom3dPartyCalendarsEventSeries({
          variables: { eventId, emails }
        })
      } else {
        await removeAttendeesFrom3dPartyCalendars({
          variables: { eventId, emails }
        })
      }

      refetchInvitees()
      hideModal()
    })
  }

  const confirmResendInvitation = async () => {
    const { refetchInvitees, selectedInvitees } = selectProps
    openMarkAsAcceptedModal(MarkAsAcceptedModal, {
      selectedInvitees: selectedInvitees
    }).then(async ({ sendEventRegistrationEmail }) => {
      if (sendEventRegistrationEmail) {
        await sendEventRegistration(selectProps)
      }

      await setAttending(selectProps, true)

      refetchInvitees()
      hideModal()
    })
  }

  const resendInvitation = async (props: SelectProps) => {
    const { selectedInvitees } = props

    if (isRecurringSeries) {
      selectedInvitees.map(async (selectedInvitee) => {
        await resendInvitationEmailEventSeries({
          variables: {
            userId: selectedInvitee.user_id,
            eventId: props.eventId
          }
        })
      })
    } else {
      await Promise.all(
        selectedInvitees.map(async (selectedInvitee) => {
          await resendInvitationEmail({
            variables: {
              userId: selectedInvitee.user_id,
              eventId: props.eventId
            }
          })
        })
      )
    }
    notification.info(
      t`Sent ${selectedInvitees.length} event invitation${
        selectedInvitees.length === 0 ? '' : 's'
      }`
    )
  }

  const sendEventRegistration = async (props: SelectProps) => {
    const { selectedInvitees } = props

    await Promise.all(
      selectedInvitees.map(async (selectedInvitee) => {
        await sendEventRegistrationEmailMutation({
          variables: {
            userId: selectedInvitee.user_id,
            eventId: props.eventId
          }
        })
      })
    )

    notification.info(
      t`Sent ${selectedInvitees.length} event invitation${
        selectedInvitees.length === 0 ? '' : 's'
      }`
    )
  }

  const setPresent = async (props: SelectProps, present: boolean | null) => {
    if (isRecurringSeries) {
      await Promise.all(
        props.selectedInvitees.map(async (selectedInvitee) => {
          await setEventInviteePresentFlagEventSeries({
            variables: {
              userId: selectedInvitee.user_id,
              eventId: props.eventId,
              groupId: selectedInvitee.group_id ?? props.groupId,
              present
            }
          })
        })
      )
    } else {
      await Promise.all(
        props.selectedInvitees.map(async (selectedInvitee) => {
          await setEventInviteePresentFlag({
            variables: {
              userId: selectedInvitee.user_id,
              eventId: props.eventId,
              groupId: selectedInvitee.group_id ?? props.groupId,
              present
            }
          })
        })
      )
    }

    selectProps.refetchInvitees()
  }

  const setAttending = async (
    props: SelectProps,
    attending: boolean | null
  ) => {
    if (isRecurringSeries) {
      await Promise.all(
        props.selectedInvitees.map(async (selectedInvitee) => {
          await setEventInviteeAttendanceFlagEventSeries({
            variables: {
              userId: selectedInvitee.user_id,
              eventId: props.eventId,
              groupId: selectedInvitee.group_id ?? props.groupId,
              attending
            }
          })
        })
      )
    } else {
      await Promise.all(
        props.selectedInvitees.map(async (selectedInvitee) => {
          await setEventInviteeAttendanceFlag({
            variables: {
              userId: selectedInvitee.user_id,
              eventId: props.eventId,
              groupId: selectedInvitee.group_id ?? props.groupId,
              attending
            }
          })
        })
      )
    }

    await syncAttendeesStatusWith3dPartyCalendars({
      variables: { eventId: props.eventId }
    })
    props.refetchInvitees()
  }

  const getRemoveButtonText = () => {
    if (isRecurringSeries) {
      return <Trans>Remove from event series</Trans>
    }
    if (isRecurringInstance) {
      return <Trans>Remove from event occurrence</Trans>
    }
    return <Trans>Remove from event</Trans>
  }

  return (
    <Box className="InviteeActionsMenuList Dropdown">
      {!isRecurringSeries && (
        <>
          <Box className="DropdownLabel">RSVP</Box>
          <Box
            className="DropdownLink invitee-mark-as-attending"
            onClick={async () => {
              await confirmResendInvitation()
            }}
          >
            <Box {...styleDot} backgroundColor={brandColors.green.mid} />{' '}
            <Trans>Mark as Accepted</Trans>
          </Box>
          <Box
            className="DropdownLink invitee-mark-as-not-attending"
            onClick={async () => {
              await setAttending(selectProps, false)
            }}
          >
            <Box {...styleDot} backgroundColor={brandColors.red.mid} />
            <Trans> Mark as Declined</Trans>
          </Box>

          <Box
            className="DropdownLink invitee-mark-as-attending"
            onClick={async () => {
              await setAttending(selectProps, null)
            }}
          >
            <IconCircleEmpty /> <Trans>Mark as No response</Trans>
          </Box>
        </>
      )}

      {!isRecurringSeries && allowAttendance && (
        <>
          <hr className="DropdownDivider" />
          <Box className="DropdownLabel">
            <Trans>Attendance</Trans>
          </Box>
          <Box
            className="DropdownLink invitee-mark-as-attended"
            onClick={async () => {
              await setPresent(selectProps, true)
            }}
          >
            <IconCheckFull /> <Trans>Mark as Attended</Trans>
          </Box>
          <Box
            className="DropdownLink invitee-mark-as-did-not-attended"
            onClick={async () => {
              await setPresent(selectProps, false)
            }}
          >
            <IconXFull /> <Trans>Mark as Did not attend</Trans>
          </Box>

          <hr className="DropdownDivider" />
        </>
      )}
      <Box className="DropdownLabel">
        <Trans>Actions</Trans>
      </Box>
      <Box
        className="DropdownLink invitee-resend"
        onClick={async () => {
          await resendInvitation(selectProps)
        }}
      >
        <Trans>Resend invitation</Trans>
      </Box>

      <Tooltip
        text={
          reasonNotAllowRemove
            ? t`People invited via a ${reasonNotAllowRemove} cannot be removed.`
            : null
        }
      >
        <Box
          className={classNames('DropdownLink invitee-removal', {
            disable: !allowRemove
          })}
          onClick={(ev) => {
            if (!allowRemove) {
              ev.stopPropagation()
              return false
            }
            confirmInviteeRemoval(eventId)
          }}
        >
          {getRemoveButtonText()}
        </Box>
      </Tooltip>
      <Portal>{removeModal}</Portal>
      <Portal>{markAsAcceptedModal}</Portal>
    </Box>
  )
}
